import React, { useState, useMemo } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
} from 'date-fns';

interface ViewingEvent {
  id: string;
  prospectName: string;
  propertyTitle: string;
  time: string; // e.g., "18:00"
  status: 'confirmed' | 'pending' | 'available' | 'booked';
  email: string;
  phone: string;
  date: Date;
}

interface ViewingCalendarProps {
  viewings: ViewingEvent[];
  onClose?: () => void;
  onSelectViewing?: (viewing: ViewingEvent) => void;
  onAddViewing?: (date: Date) => void;
  onRemoveViewing?: (viewingId: string) => void;
  onUpdateStatus?: (
    viewingId: string,
    status: 'confirmed' | 'pending' | 'attended' | 'no-show' | 'cancelled'
  ) => void;
  isModal?: boolean;
}

const statusColors = {
  confirmed: 'bg-green-50 border-green-200 text-green-700',
  pending: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  available: 'bg-blue-50 border-blue-200 text-blue-700',
  booked: 'bg-gray-50 border-gray-200 text-gray-700',
};

const statusDotColors = {
  confirmed: 'bg-green-500',
  pending: 'bg-yellow-500',
  available: 'bg-blue-500',
  booked: 'bg-gray-400',
};

const ViewingCalendar: React.FC<ViewingCalendarProps> = ({
  viewings,
  onClose,
  onSelectViewing,
  onAddViewing,
  onRemoveViewing,
  onUpdateStatus,
  isModal = false,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedViewing, setSelectedViewing] = useState<ViewingEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get viewings for each day
  const viewingsByDay = useMemo(() => {
    const map = new Map<string, ViewingEvent[]>();
    viewings.forEach((viewing) => {
      const dateKey = format(viewing.date, 'yyyy-MM-dd');
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)?.push(viewing);
    });
    return map;
  }, [viewings]);

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  return (
    <div
      className={
        isModal ? 'fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4' : ''
      }
    >
      <div
        className={`${
          isModal
            ? 'bg-white rounded-lg shadow-lg w-full max-w-full sm:max-w-4xl max-h-[90vh] flex flex-col'
            : 'bg-white rounded-xl border border-gray-200'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">{format(currentDate, 'MMMM yyyy')}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5 text-gray-600" />
            </button>
            {isModal && onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors ml-2"
              >
                <XMarkIcon className="w-5 h-5 text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Calendar Grid */}
        <div className={isModal ? 'flex-1 p-4 sm:p-6' : 'p-4 sm:p-6'}>
          {/* Scroll wrapper for small screens */}
          <div className="-mx-4 sm:mx-0 overflow-x-auto">
            <div className="min-w-[720px] px-4 sm:px-0">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2 sm:mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div
                    key={day}
                    className="text-center font-semibold text-gray-600 text-xs sm:text-sm p-2"
                    aria-label={day}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-2">
                {daysInMonth.map((day) => {
                  const dateKey = format(day, 'yyyy-MM-dd');
                  const dayViewings = viewingsByDay.get(dateKey) || [];
                  const isCurrentMonth = isSameMonth(day, currentDate);

                  return (
                    <div
                      key={dateKey}
                      onClick={() => setSelectedDate(day)}
                      className={`min-h-28 sm:min-h-32 rounded-lg border-2 p-2 cursor-pointer transition-all ${
                        selectedDate && isSameDay(selectedDate, day)
                          ? 'border-purple-500 bg-purple-50'
                          : isCurrentMonth
                            ? 'bg-white border-gray-200 hover:border-purple-300'
                            : 'bg-gray-50 border-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1 sm:mb-2">
                        <div
                          className={`text-sm font-semibold ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}
                        >
                          {format(day, 'd')}
                        </div>
                        {isCurrentMonth && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDate(day);
                              onAddViewing?.(day);
                            }}
                            className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-100 rounded transition-colors touch-manipulation"
                            title="Add viewing"
                            aria-label={`Add viewing on ${format(day, 'MMM d')}`}
                          >
                            <PlusIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* Viewing Events */}
                      <div className="space-y-1 text-xs">
                        {dayViewings.slice(0, 3).map((viewing) => (
                          <button
                            key={viewing.id}
                            onClick={() => {
                              setSelectedViewing(viewing);
                              onSelectViewing?.(viewing);
                            }}
                            className={`block w-full text-left p-1.5 rounded border-l-2 truncate hover:shadow-sm transition-all ${statusColors[viewing.status]}`}
                            title={`${viewing.prospectName} - ${viewing.time}`}
                            aria-label={`${viewing.time} ${viewing.prospectName}`}
                          >
                            <div className="flex items-center gap-1">
                              <div
                                className={`w-2 h-2 rounded-full ${statusDotColors[viewing.status]}`}
                              />
                              <span className="truncate font-medium">{viewing.time}</span>
                            </div>
                            <div className="truncate text-xs opacity-75">
                              {viewing.prospectName}
                            </div>
                          </button>
                        ))}
                        {dayViewings.length > 3 && (
                          <div className="text-gray-500 pl-2">+{dayViewings.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Selected Viewing Details */}
        {selectedViewing && (
          <div className="border-t border-gray-200 p-6 bg-gradient-to-br from-purple-50 to-blue-50">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Viewing Details</h3>
              <button
                onClick={() => setSelectedViewing(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Property</p>
                <p className="font-semibold text-gray-900">{selectedViewing.propertyTitle}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Prospect</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  {selectedViewing.prospectName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <ClockIcon className="w-4 h-4" />
                  {format(selectedViewing.date, 'MMM d, yyyy')} at {selectedViewing.time}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedViewing.status]}`}
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  {selectedViewing.status.charAt(0).toUpperCase() + selectedViewing.status.slice(1)}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Email</p>
                <a
                  href={`mailto:${selectedViewing.email}`}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  {selectedViewing.email}
                </a>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Phone</p>
                <a
                  href={`tel:${selectedViewing.phone}`}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  {selectedViewing.phone}
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                onClick={() => {
                  window.location.href = `mailto:${selectedViewing.email}?subject=Property Viewing - ${selectedViewing.propertyTitle}&body=Hi ${selectedViewing.prospectName},%0D%0A%0D%0ARegarding your viewing appointment for ${selectedViewing.propertyTitle} on ${format(selectedViewing.date, 'MMM d, yyyy')} at ${selectedViewing.time}.%0D%0A%0D%0ABest regards`;
                }}
                className="w-full sm:flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
              >
                Contact Prospect
              </button>
              <button
                onClick={() => {
                  if (onUpdateStatus) {
                    onUpdateStatus(selectedViewing.id, 'attended');
                    setSelectedViewing(null);
                  }
                }}
                className="w-full sm:flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Mark Attended
              </button>
              <button
                onClick={() => {
                  if (onRemoveViewing) {
                    onRemoveViewing(selectedViewing.id);
                    setSelectedViewing(null);
                  }
                }}
                className="w-full sm:flex-1 px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <TrashIcon className="w-4 h-4" />
                Remove
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewingCalendar;
